/**
 * Brand tokens «Открыто» — Spec 020 D1.
 *
 * Copied from the brandbook v1 `tokens.json` (round 10/11, agreed by both agents
 * and accepted by Kostya and Alexander on 2026-09-26). Telegram no longer picks
 * our colours; it only decides which of the two themes is shown.
 *
 * Rules the values keep (tests/brand.test.mjs recomputes them):
 * - text, text-muted and every status colour ≥ 4.5:1 on bg, surface and surface-2;
 * - border-strong and focus ≥ 3:1 on the same grounds;
 * - status tints ≥ 10 ΔE apart and ≥ 5 ΔE from any ground;
 * - yellow (`accent`, `now`) only for the one main button and «Сейчас».
 */

export type ColorScheme = "light" | "dark";

export const BRAND_TOKENS = [
  "bg", "surface", "surface-2", "text", "text-muted", "border", "border-strong",
  "accent", "on-accent", "link", "focus", "now", "on-now", "now-tint",
  "done", "done-tint", "review", "review-tint", "needs", "needs-tint",
  "blocked", "blocked-tint", "waiting", "waiting-tint",
  // The logo mark (brandbook sheet 1): the frame and the open leaf of the door.
  "mark-frame", "mark-leaf",
] as const;

export type BrandToken = (typeof BRAND_TOKENS)[number];

export const BRAND: Record<ColorScheme, Record<BrandToken, string>> = {
  light: {
    bg: "#F1EEE8", surface: "#FFFFFF", "surface-2": "#F8F6F2",
    text: "#0E3B43", "text-muted": "#56625F",
    border: "#DCD6CB", "border-strong": "#857E72",
    accent: "#FFB547", "on-accent": "#0E3B43",
    link: "#1F6F75", focus: "#1F6F75",
    now: "#FFB547", "on-now": "#0E3B43", "now-tint": "#FFE7BF",
    done: "#27704F", "done-tint": "#CEECDB",
    review: "#4A4FB8", "review-tint": "#E4E0F3",
    needs: "#A12A6B", "needs-tint": "#FFD8E8",
    blocked: "#B42318", "blocked-tint": "#FAE0DA",
    waiting: "#56625F", "waiting-tint": "#D4E2E3",
    "mark-frame": "#0E3B43", "mark-leaf": "#0E3B43",
  },
  dark: {
    bg: "#0A2A30", surface: "#0F3740", "surface-2": "#12404A",
    text: "#F4EFE6", "text-muted": "#A7BDBB",
    border: "#1D4A52", "border-strong": "#648F95",
    accent: "#FFB547", "on-accent": "#0A2A30",
    link: "#7FD1D6", focus: "#7FD1D6",
    now: "#FFB547", "on-now": "#0A2A30", "now-tint": "#3D3517",
    done: "#6FD3A6", "done-tint": "#12433A",
    review: "#AEB2FF", "review-tint": "#26305E",
    needs: "#FF8FC8", "needs-tint": "#4A1F3A",
    blocked: "#FF8F85", "blocked-tint": "#4C2226",
    waiting: "#A7BDBB", "waiting-tint": "#1D4E57",
    "mark-frame": "#F1EEE8", "mark-leaf": "#0A2A30",
  },
};

/**
 * The eleven names the components still use (design spec §1, Telegram era),
 * pointed at brand tokens so every screen changes at once (Spec 020 D1). Later
 * stages replace these names file by file with the brand ones.
 */
export const LEGACY: Record<string, BrandToken> = {
  "--bg": "bg",
  "--sbg": "surface",
  "--text": "text",
  "--hint": "text-muted",
  "--accent": "link",
  "--btn": "text",
  "--btntext": "bg",
  "--dstr": "blocked",
  "--sep": "border",
  "--accent-soft": "surface-2",
  "--dstr-soft": "blocked-tint",
};

/** Every CSS variable for one theme: `--p-<token>` plus the legacy names. */
export function brandVariables(scheme: ColorScheme): Record<string, string> {
  const palette = BRAND[scheme];
  const vars: Record<string, string> = {};
  for (const token of BRAND_TOKENS) vars[`--p-${token}`] = palette[token];
  for (const [name, token] of Object.entries(LEGACY)) vars[name] = palette[token];
  return vars;
}
